// AI Features module — analysis, smart placement, chat, BOM optimization
// Integrates with the planner toolbar and floating widgets
import { showToast, escapeHtml, authenticatedFetch } from '../shared/utils.ts'
import { t, applyTranslations, getDocumentLanguage } from '../shared/i18n.ts'
import {
  api_v1_ai_analyze_project_path,
  api_v1_ai_chat_path,
  api_v1_ai_nlp_command_path,
  api_v1_ai_optimize_bom_path,
  api_v1_ai_smart_place_path,
} from '../shared/api-routes'
import { hideModal, isOpenModal, showModal } from '../shared/modal.ts'
import { PLOT_TEMPLATES } from '../planner/templates.ts'
import { formatLength } from '../shared/units.ts'
import { trackAiAnalysis, trackAiChat } from '../shared/analytics.ts'

const RATE_LIMIT_ERROR = 'RATE_LIMIT_EXCEEDED'
const AI_OVERLAY_IDS = ['aiAnalysisOverlay', 'aiSmartPlaceOverlay', 'aiBomOverlay', 'aiNlpOverlay']

/** Resolve an i18n key or fall back to the english string. The shared t()
 *  returns the key itself on miss, so `t(key) || fallback` never falls back —
 *  the miss is a non-empty string. That shipped the raw `ai.suggestCoverage`
 *  identifier into the chat chips until the keys were added. */
function tf(key: string, fallback: string): string {
  const out = t(key)
  return out === key ? fallback : out
}

/** Where the AI action buttons live. `#aiToolbarGroup` is the blueprint
 *  chrome's slot; `#actionsToolbarGroup` was v1's and is kept only so the
 *  unit tests that build their own toolbar keep describing something real. */
function aiToolbarAnchor(): HTMLElement | null {
  return document.getElementById('aiToolbarGroup')
    ?? document.getElementById('actionsToolbarGroup')
}

/** The sidebar statistics block — `.stats-panel` in v1, the flyout group
 *  carrying `data-ai-stats-anchor` in the blueprint chrome. */
function aiStatsAnchor(): HTMLElement | null {
  return document.querySelector<HTMLElement>('[data-ai-stats-anchor]')
    ?? document.querySelector<HTMLElement>('.stats-panel')
}

// ── Helpers ──────────────────────────────────────────────────────────

interface RateLimitedError extends Error {
  rateLimited?: boolean
}

function isRateLimited(e: unknown): boolean {
  return !!(e && typeof e === 'object' && (e as RateLimitedError).rateLimited)
}

function checkRateLimitError(resp: Response) {
  if (resp.status === 429) {
    showToast(t('ai.rateLimitExceeded'), 'warning')
    const err = new Error(RATE_LIMIT_ERROR) as RateLimitedError
    err.rateLimited = true
    throw err
  }
}

function getProjectState() {
  if (window.__smartpluvia) {
    return (window as any).__smartpluvia.getState()
  }
  return null
}

function getProjectId() {
  if (window.__smartpluvia) {
    return (window as any).__smartpluvia.getProjectId()
  }
  return null
}

function renderMarkdown(text: string | null | undefined) {
  if (!text) return ''
  return escapeHtml(text)
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/`(.+?)`/g, '<code>$1</code>')
    .replace(/^### (.+)$/gm, '<h4>$1</h4>')
    .replace(/^## (.+)$/gm, '<h3>$1</h3>')
    .replace(/^# (.+)$/gm, '<h2>$1</h2>')
    .replace(/^- (.+)$/gm, '<li>$1</li>')
    .replace(/(<li>.*<\/li>)/gs, '<ul>$1</ul>')
    .replace(/\n/g, '<br>')
}

// ── 1. AI Analysis ──────────────────────────────────────────────────

function createAnalysisButton() {
  const actionsGroup = aiToolbarAnchor()
  if (!actionsGroup) return
  if (document.getElementById('btnAiAnalysis')) return

  const btn = document.createElement('button')
  btn.id = 'btnAiAnalysis'
  btn.className = 'action-btn ai-action-btn'
  btn.setAttribute('title', t('ai.analyzeTitle'))
  btn.setAttribute('aria-label', t('ai.analyze'))
  btn.setAttribute('data-i18n', '[aria-label]ai.analyze;[title]ai.analyzeTitle')
  btn.innerHTML = `
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <path d="M12 2a4 4 0 0 1 4 4c0 1.95-1.4 3.58-3.25 3.93L12 22"/>
      <path d="M8 6a4 4 0 0 0 0 8"/>
      <path d="M16 6a4 4 0 0 1 0 8"/>
    </svg>
    <span data-i18n="ai.analyze">AI</span>
  `

  actionsGroup.appendChild(btn)
}

function createAnalysisModal() {
  if (document.getElementById('aiAnalysisOverlay')) return

  const overlay = document.createElement('div')
  overlay.id = 'aiAnalysisOverlay'
  overlay.className = 'ai-overlay'
  overlay.hidden = true

  overlay.innerHTML = `
    <div class="ai-modal ai-analysis-modal" role="dialog" aria-labelledby="aiAnalysisTitle" aria-modal="true">
      <div class="ai-modal-header">
        <h3 id="aiAnalysisTitle" data-i18n="ai.analysisTitle">${t('ai.analysisTitle')}</h3>
        <button class="ai-modal-close" id="aiAnalysisClose" data-i18n="[aria-label]common.close" aria-label="Close">&times;</button>
      </div>
      <div class="ai-modal-body" id="aiAnalysisBody">
        <div class="ai-loading" id="aiAnalysisLoading" hidden>
          <div class="ai-spinner"></div>
          <p data-i18n="ai.analyzing">${t('ai.analyzing')}</p>
        </div>
        <div id="aiAnalysisResults" hidden></div>
        <div id="aiAnalysisEmpty">
          <p data-i18n="ai.analysisDesc">${t('ai.analysisDesc')}</p>
          <button class="ai-btn-primary" id="aiAnalysisStart" data-i18n="ai.startAnalysis">${t('ai.startAnalysis')}</button>
        </div>
      </div>
    </div>
  `
  document.body.appendChild(overlay)
}

/** Presentation for a category's status.
 *
 * `ProjectAnalysisService` emits the enum `ok | warning | error | info`
 * (app/services/project_analysis_service.rb, enforced by the structured-output
 * schema in app/services/ai/schemas/project_analysis.rb), but this renderer
 * was comparing against `'good'` — the name of the *CSS* modifier. Every
 * healthy category therefore fell through to the error branch and rendered a
 * red ✗, and `ai-category--ok` / `--info` match no rule in ai-features.css so
 * the card lost its status colour entirely.
 *
 * Going through a lookup also keeps the modifier off the raw-interpolation
 * path: only these fixed strings can reach the `class="…"` attribute below. */
const CATEGORY_STATUS_PRESENTATION: Record<string, { icon: string; modifier: string }> = {
  ok: { icon: '&#10003;', modifier: 'good' },
  good: { icon: '&#10003;', modifier: 'good' },
  warning: { icon: '&#9888;', modifier: 'warning' },
  error: { icon: '&#10007;', modifier: 'error' },
  info: { icon: '&#8505;', modifier: 'info' },
}

function renderAnalysisResults(data: any) {
  const container = document.getElementById('aiAnalysisResults')
  if (!container) return

  // The score reaches innerHTML and an SVG attribute; coerce it so a
  // non-numeric value can neither inject markup nor render
  // `stroke-dasharray="NaN 327"` (which drops the ring silently).
  const score = Number(data.score)
  const safeScore = Number.isFinite(score) ? Math.max(0, Math.min(100, score)) : 0
  const scoreClass = safeScore >= 80 ? 'good' : safeScore >= 50 ? 'warning' : 'error'
  const scoreLabel = t('ai.score')

  let html = `
    <div class="ai-score ai-score--${scoreClass}">
      <div class="ai-score-ring">
        <svg viewBox="0 0 120 120" width="120" height="120">
          <circle cx="60" cy="60" r="52" fill="none" stroke="var(--border)" stroke-width="8"/>
          <circle cx="60" cy="60" r="52" fill="none" stroke="currentColor" stroke-width="8"
            stroke-dasharray="${(safeScore / 100) * 327} 327"
            stroke-linecap="round" transform="rotate(-90 60 60)"/>
        </svg>
        <span class="ai-score-value">${safeScore}</span>
      </div>
      <span class="ai-score-label">${scoreLabel}</span>
    </div>
  `

  if (data.categories && data.categories.length > 0) {
    html += '<div class="ai-categories">'
    for (const cat of data.categories) {
      const presentation = CATEGORY_STATUS_PRESENTATION[cat.status] ?? CATEGORY_STATUS_PRESENTATION.info
      const statusIcon = presentation.icon
      const statusClass = presentation.modifier

      html += `
        <div class="ai-category ai-category--${statusClass}">
          <div class="ai-category-header">
            <span class="ai-category-icon">${statusIcon}</span>
            <span class="ai-category-name">${escapeHtml(cat.name)}</span>
            <span class="ai-category-status">${escapeHtml(cat.status)}</span>
          </div>
      `

      if (cat.issues && cat.issues.length > 0) {
        html += '<ul class="ai-category-issues">'
        for (const issue of cat.issues) {
          html += `<li>${escapeHtml(issue)}</li>`
        }
        html += '</ul>'
      }

      if (cat.recommendations && cat.recommendations.length > 0) {
        html += `<div class="ai-recommendations">`
        html += `<strong>${t('ai.recommendations')}:</strong>`
        html += '<ul>'
        for (const rec of cat.recommendations) {
          html += `<li>${renderMarkdown(rec)}</li>`
        }
        html += '</ul></div>'
      }

      html += '</div>'
    }
    html += '</div>'
  }

  if (data.summary) {
    html += `<div class="ai-summary">${renderMarkdown(data.summary)}</div>`
  }

  container.innerHTML = html
}

async function runAnalysis() {
  const loading = document.getElementById('aiAnalysisLoading')
  const results = document.getElementById('aiAnalysisResults')
  const empty = document.getElementById('aiAnalysisEmpty')

  if (loading) loading.hidden = false
  if (results) results.hidden = true
  if (empty) empty.hidden = true

  // Ai::AnalyzeContract requires a boundary, so a plan with heads but no
  // outline used to go out anyway and come back as a bare 400.
  const state = getProjectState()
  if (!state || state.boundary.length === 0) {
    showToast(t(state ? 'ai.noBoundary' : 'ai.noProjectData'), 'warning')
    if (loading) loading.hidden = true
    if (empty) empty.hidden = false
    return
  }

  try {
    const resp = await authenticatedFetch(api_v1_ai_analyze_project_path(), {
      method: 'POST',
      body: {
        project_id: getProjectId(),
        boundary: state.boundary,
        sprinklers: state.sprinklers,
        zones: state.zones,
        pipes: state.pipes,
        trees: state.trees,
        valve_objects: state.valveObjects,
        plot_width: state.plotWidth,
        plot_height: state.plotHeight,
      },
    })

    checkRateLimitError(resp)
    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}))
      throw new Error(err.error || `HTTP ${resp.status}`)
    }

    const data = await resp.json()
    renderAnalysisResults(data.analysis || data)
    if (results) results.hidden = false
    trackAiAnalysis()
  } catch (e) {
    if (!isRateLimited(e)) showToast(t('ai.analysisError'), 'error')
    if (empty) empty.hidden = false
  } finally {
    if (loading) loading.hidden = true
  }
}

// ── 2. Smart Placement ──────────────────────────────────────────────

function createSmartPlaceButton() {
  const actionsGroup = aiToolbarAnchor()
  if (!actionsGroup) return
  if (document.getElementById('btnAiSmartPlace')) return

  const btn = document.createElement('button')
  btn.id = 'btnAiSmartPlace'
  btn.className = 'action-btn ai-action-btn'
  btn.setAttribute('title', t('ai.smartPlaceTitle'))
  btn.setAttribute('aria-label', t('ai.smartPlace'))
  btn.setAttribute('data-i18n', '[aria-label]ai.smartPlace;[title]ai.smartPlaceTitle')
  btn.innerHTML = `
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="3"/>
      <path d="M12 2v4M12 18v4M2 12h4M18 12h4"/>
      <path d="M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
    </svg>
    <span data-i18n="ai.smartPlace">AI Place</span>
  `

  actionsGroup.appendChild(btn)
}

function createSmartPlaceModal() {
  if (document.getElementById('aiSmartPlaceOverlay')) return

  const overlay = document.createElement('div')
  overlay.id = 'aiSmartPlaceOverlay'
  overlay.className = 'ai-overlay'
  overlay.hidden = true

  // The model picker lists exactly SmartPlacementService::MODELS — the models
  // the service can lay a plot out for (spec/services/smart_placement_service_spec.rb
  // holds the two together). It offered four more, which the service dropped.
  overlay.innerHTML = `
    <div class="ai-modal ai-smartplace-modal" role="dialog" aria-labelledby="aiSmartPlaceTitle" aria-modal="true">
      <div class="ai-modal-header">
        <h3 id="aiSmartPlaceTitle" data-i18n="ai.smartPlaceTitle">${t('ai.smartPlaceTitle')}</h3>
        <button class="ai-modal-close" id="aiSmartPlaceClose" data-i18n="[aria-label]common.close" aria-label="Close">&times;</button>
      </div>
      <div class="ai-modal-body">
        <div class="ai-loading" id="aiSmartPlaceLoading" hidden>
          <div class="ai-spinner"></div>
          <p data-i18n="ai.placingSprinklers">${t('ai.placingSprinklers')}</p>
        </div>
        <div id="aiSmartPlaceForm">
          <div class="ai-form-field">
            <label for="aiPreferredModel" data-i18n="ai.preferredModel">${t('ai.preferredModel')}</label>
            <select id="aiPreferredModel" class="ai-select">
              <option value="" data-i18n="ai.anyModel">${t('ai.anyModel')}</option>
              <option value="PGP-R4">Hunter PGP-R4</option>
              <option value="RB-5000-2">Rain Bird 5000</option>
              <option value="MP3000">Hunter MP3000</option>
              <option value="Pro-15">Hunter Pro-15</option>
              <option value="MP1000">Hunter MP1000</option>
              <option value="RB-U10">Rain Bird U-10</option>
            </select>
          </div>
          <div class="ai-form-field">
            <label for="aiBudget" data-i18n="ai.budgetLabel">${t('ai.budgetLabel')}</label>
            <div class="ai-input-group">
              <input type="number" id="aiBudget" class="ai-input" min="0" step="100"
                placeholder="${t('ai.budgetPlaceholder')}"
                data-i18n="[placeholder]ai.budgetPlaceholder">
              <span class="ai-input-suffix">USD</span>
            </div>
          </div>
          <button class="ai-btn-primary" id="aiSmartPlaceStart" data-i18n="ai.generatePlacement">${t('ai.generatePlacement')}</button>
        </div>
        <div id="aiSmartPlaceResults" hidden>
          <div class="ai-placement-info" id="aiPlacementInfo"></div>
          <div class="ai-placement-actions">
            <button class="ai-btn-secondary" id="aiPlacementReject" data-i18n="ai.reject">${t('ai.reject')}</button>
            <button class="ai-btn-primary" id="aiPlacementAccept" data-i18n="ai.accept">${t('ai.accept')}</button>
          </div>
        </div>
      </div>
    </div>
  `
  document.body.appendChild(overlay)
}

let pendingPlacement: Record<string, unknown>[] | null = null
let previewSprinklerEls: SVGElement[] = []

function clearPlacementPreview() {
  for (const el of previewSprinklerEls) {
    el.remove()
  }
  previewSprinklerEls = []
}

function renderPlacementPreview(sprinklers: any) {
  clearPlacementPreview()
  const canvas = document.getElementById('canvas')
  if (!canvas) return

  const state = getProjectState()
  const pxPerMeter = state ? state.pxPerMeter : 40

  for (const sp of sprinklers) {
    const cx = sp.x * pxPerMeter
    const cy = sp.y * pxPerMeter

    // Outer coverage circle (semi-transparent)
    const coverage = document.createElementNS('http://www.w3.org/2000/svg', 'circle')
    coverage.setAttribute('cx', String(cx))
    coverage.setAttribute('cy', String(cy))
    coverage.setAttribute('r', String((sp.radius || 4) * pxPerMeter))
    coverage.setAttribute('class', 'ai-preview-coverage')
    canvas.appendChild(coverage)
    previewSprinklerEls.push(coverage)

    // Sprinkler dot
    const dot = document.createElementNS('http://www.w3.org/2000/svg', 'circle')
    dot.setAttribute('cx', String(cx))
    dot.setAttribute('cy', String(cy))
    dot.setAttribute('r', '6')
    dot.setAttribute('class', 'ai-preview-sprinkler')
    canvas.appendChild(dot)
    previewSprinklerEls.push(dot)

    // Label
    const label = document.createElementNS('http://www.w3.org/2000/svg', 'text')
    label.setAttribute('x', String(cx + 10))
    label.setAttribute('y', String(cy - 10))
    label.setAttribute('class', 'ai-preview-label')
    label.textContent = sp.model || 'AI'
    canvas.appendChild(label)
    previewSprinklerEls.push(label)
  }
}

// Irrigo::Placement::Scorer reports what is wrong with a layout as codes —
// violations are hard failures (a head inside an obstacle), warnings the
// tradeable ones (overlap, overspray) — and the figures sit in the same report.
// A code this build does not know is skipped rather than shown raw.
type QualityReport = Record<string, unknown>
const QUALITY_NOTES = new Map<string, (q: QualityReport) => string>([
  ['heads_in_obstacles', (q) => t('ai.qualityHeadsInObstacles', { count: Number(q.heads_in_obstacles) })],
  ['heads_outside_boundary', (q) => t('ai.qualityHeadsOutsideBoundary', { count: Number(q.heads_outside_boundary) })],
  ['low_coverage', () => t('ai.qualityLowCoverage')],
  ['unscorable', () => t('ai.qualityUnscorable')],
  ['heavy_overlap', (q) => t('ai.qualityHeavyOverlap', { value: Number(q.mean_multiplicity) })],
  ['over_watered', (q) => t('ai.qualityOverWatered', { value: Math.round(Number(q.over_watered_ratio) * 100) })],
  ['overspray', (q) => t('ai.qualityOverspray', { value: Math.round(Number(q.overspray_ratio) * 100) })],
])

function qualityNotes(quality: QualityReport | undefined): HTMLElement[] {
  if (!quality) return []
  const levels: Array<[string, unknown]> = [['violation', quality.violations], ['warning', quality.warnings]]
  return levels.flatMap(([level, codes]) =>
    (Array.isArray(codes) ? codes : []).flatMap((code) => {
      const note = QUALITY_NOTES.get(String(code))
      if (!note) return []
      const el = document.createElement('p')
      el.className = `ai-placement-note ai-placement-note--${level}`
      el.textContent = note(quality)
      return [el]
    }),
  )
}

async function runSmartPlace() {
  const loading = document.getElementById('aiSmartPlaceLoading')
  const form = document.getElementById('aiSmartPlaceForm')
  const results = document.getElementById('aiSmartPlaceResults')
  const infoEl = document.getElementById('aiPlacementInfo')

  const state = getProjectState()
  if (!state || state.boundary.length === 0) {
    showToast(t('ai.noBoundary'), 'warning')
    return
  }

  const preferredModel = (document.getElementById('aiPreferredModel') as HTMLInputElement | null)?.value || ''
  const budget = (document.getElementById('aiBudget') as HTMLInputElement | null)?.value || null

  if (form) form.hidden = true
  if (loading) loading.hidden = false
  if (results) results.hidden = true

  try {
    const resp = await authenticatedFetch(api_v1_ai_smart_place_path(), {
      method: 'POST',
      body: {
        project_id: getProjectId(),
        boundary: state.boundary,
        trees: state.trees,
        valve_objects: state.valveObjects,
        plot_width: state.plotWidth,
        plot_height: state.plotHeight,
        preferred_model: preferredModel || undefined,
        budget: budget ? parseFloat(budget) : undefined,
      },
    })

    checkRateLimitError(resp)
    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}))
      throw new Error(err.error || `HTTP ${resp.status}`)
    }

    const raw = await resp.json()
    const data = raw.placement || raw
    pendingPlacement = data.sprinklers || []

    // Show preview on canvas
    renderPlacementPreview(pendingPlacement)

    // Show result info
    if (infoEl) {
      const countLabel = t('ai.sprinklersProposed')
      const coverageLabel = t('ai.estimatedCoverage')
      // The service scores the layout it returns (Irrigo::Placement::Scorer)
      // and sends that as `quality`; nothing produces a top-level `coverage`
      // or a cost since the positions stopped coming from the model (#1595).
      // Coerced to a number before it reaches innerHTML.
      const coverage = Math.round(Number(data.quality?.coverage_ratio) * 100)
      infoEl.innerHTML = `
        <div class="ai-placement-stat">
          <span>${countLabel}:</span>
          <strong>${pendingPlacement!.length}</strong>
        </div>
        ${Number.isFinite(coverage) && coverage ? `
          <div class="ai-placement-stat">
            <span>${coverageLabel}:</span>
            <strong>${coverage}%</strong>
          </div>
        ` : ''}
      `
      infoEl.append(...qualityNotes(data.quality))
    }

    if (results) results.hidden = false
  } catch (e) {
    if (!isRateLimited(e)) showToast(t('ai.placementError'), 'error')
    if (form) form.hidden = false
  } finally {
    if (loading) loading.hidden = true
  }
}

function acceptPlacement() {
  if (!pendingPlacement || pendingPlacement.length === 0) return

  // The service's `zoneId` is its own grouping counted from one, not a zone of
  // this plan; passed on, it attached heads to whichever drawn zone carried
  // that id. Without it the planner zones each head by where it stands.
  if (window.__smartpluvia && window.__smartpluvia.addSprinklers) {
    const heads = pendingPlacement.map(({ zoneId: _grouping, ...head }: { zoneId?: unknown }) => head);
    (window as any).__smartpluvia.addSprinklers(heads)
  }

  clearPlacementPreview()
  pendingPlacement = null
  showToast(t('ai.placementAccepted'), 'success')

  hideModal('aiSmartPlaceOverlay')
}

function rejectPlacement() {
  clearPlacementPreview()
  pendingPlacement = null

  const form = document.getElementById('aiSmartPlaceForm')
  const results = document.getElementById('aiSmartPlaceResults')
  if (form) form.hidden = false
  if (results) results.hidden = true
}

// ── 3. AI Chat Widget ───────────────────────────────────────────────

export const MAX_CHAT_MESSAGES = 20
type ChatRole = 'user' | 'assistant' | 'system'
type ChatMessage = { role: ChatRole; content: string }

export const chatMessages: ChatMessage[] = []

export function trimChatHistory() {
  if (chatMessages.length <= MAX_CHAT_MESSAGES) return

  const removed = chatMessages.splice(0, chatMessages.length - MAX_CHAT_MESSAGES)

  // Prepend a system summary of trimmed context
  const topics = removed
    .filter((m) => m.role === 'user')
    .map((m) => m.content)
    .slice(-3)
    .join('; ')

  if (topics) {
    chatMessages.unshift({
      role: 'system',
      content: `Previous conversation context (${removed.length} messages trimmed): user discussed — ${topics}`
    })
  }

  showTrimmedIndicator()
}

function showTrimmedIndicator() {
  const container = document.getElementById('aiChatMessages')
  if (!container) return
  if (container.querySelector('.ai-chat-trimmed')) return

  const indicator = document.createElement('div')
  indicator.className = 'ai-chat-trimmed'
  indicator.textContent = t('ai.chatTrimmed')

  const firstMsg = container.querySelector('.ai-chat-msg')
  if (firstMsg) {
    container.insertBefore(indicator, firstMsg)
  } else {
    container.prepend(indicator)
  }
}

function createChatButton() {
  if (document.getElementById('aiChatBtn')) return

  const btn = document.createElement('button')
  btn.id = 'aiChatBtn'
  btn.className = 'ai-chat-btn'
  btn.setAttribute('aria-label', t('ai.chat'))
  btn.setAttribute('data-i18n', '[aria-label]ai.chat')
  btn.innerHTML = `
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
    </svg>
  `
  document.body.appendChild(btn)
}

function createChatPanel() {
  if (document.getElementById('aiChatPanel')) return

  const panel = document.createElement('div')
  panel.id = 'aiChatPanel'
  panel.className = 'ai-chat-panel'
  panel.hidden = true

  const suggestionsHtml = [
    { key: 'ai.suggestCoverage', fallback: 'How can I improve coverage?' },
    { key: 'ai.suggestZones', fallback: 'How many zones do I need?' },
    { key: 'ai.suggestPipes', fallback: 'What pipe diameter should I choose?' },
  ].map(s => {
    const label = tf(s.key, s.fallback)
    // Both bindings matter: the attribute carries the prompt that gets sent,
    // the bare key re-translates the visible label. With only the attribute
    // form the chip kept the English text it was built with — the panel is
    // constructed before the locale blob lands.
    return `<button class="ai-chip" data-message="${escapeHtml(label)}" data-i18n="[data-message]${s.key};${s.key}">${escapeHtml(label)}</button>`
  }).join('')

  // A non-modal dialog: it floats beside the planner rather than over it,
  // but keys typed inside it are not planner shortcuts.
  panel.setAttribute('role', 'dialog')
  panel.setAttribute('aria-labelledby', 'aiChatTitle')
  panel.innerHTML = `
    <div class="ai-chat-header">
      <h4 id="aiChatTitle" data-i18n="ai.chatTitle">${t('ai.chatTitle')}</h4>
      <button class="ai-chat-close" id="aiChatClose" data-i18n="[aria-label]common.close" aria-label="Close">&times;</button>
    </div>
    <div class="ai-chat-messages" id="aiChatMessages">
      <div class="ai-chat-welcome">
        <p data-i18n="ai.chatWelcome">${t('ai.chatWelcome')}</p>
        <div class="ai-chips">${suggestionsHtml}</div>
      </div>
    </div>
    <div class="ai-chat-input-area">
      <input type="text" id="aiChatInput" class="ai-chat-input"
        placeholder="${t('ai.chatPlaceholder')}"
        data-i18n="[placeholder]ai.chatPlaceholder"
        autocomplete="off">
      <button class="ai-chat-send" id="aiChatSend" aria-label="Send" data-i18n="[aria-label]ai.send">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <line x1="22" y1="2" x2="11" y2="13"/>
          <polygon points="22 2 15 22 11 13 2 9 22 2"/>
        </svg>
      </button>
    </div>
  `
  document.body.appendChild(panel)
}

function appendChatMessage(role: ChatRole, text: string) {
  const container = document.getElementById('aiChatMessages')
  if (!container) return

  // Hide welcome on first message
  const welcome = container.querySelector('.ai-chat-welcome')
  if (welcome) (welcome as HTMLElement).hidden = true

  const msgEl = document.createElement('div')
  msgEl.className = `ai-chat-msg ai-chat-msg--${role}`

  if (role === 'assistant') {
    msgEl.innerHTML = `<div class="ai-chat-msg-content">${renderMarkdown(text)}</div>`
  } else {
    msgEl.innerHTML = `<div class="ai-chat-msg-content">${escapeHtml(text)}</div>`
  }

  container.appendChild(msgEl)
  container.scrollTop = container.scrollHeight
}

function showChatTyping() {
  const container = document.getElementById('aiChatMessages')
  if (!container) return null

  const typing = document.createElement('div')
  typing.className = 'ai-chat-msg ai-chat-msg--assistant ai-chat-typing'
  typing.innerHTML = `
    <div class="ai-chat-msg-content">
      <span class="ai-typing-dots">
        <span></span><span></span><span></span>
      </span>
    </div>
  `
  container.appendChild(typing)
  container.scrollTop = container.scrollHeight
  return typing
}

// One question at a time. The disabled send button already said so, but Enter
// in the input (and the suggestion chips) went around it: the second question
// went out without the first answer, the replies landed out of order, and the
// model was billed twice. The typed text stays in the input for afterwards.
let chatPending = false

async function sendChatMessage(text: string) {
  if (!text.trim() || chatPending) return

  chatMessages.push({ role: 'user', content: text })
  appendChatMessage('user', text)
  trimChatHistory()

  const input = document.getElementById('aiChatInput') as HTMLInputElement | null
  const sendBtn = document.getElementById('aiChatSend')
  if (input) input.value = ''
  if (sendBtn) (sendBtn as HTMLButtonElement).disabled = true

  const typingEl = showChatTyping()

  chatPending = true
  try {
    const state = getProjectState()
    const context = state ? {
      boundary_points: state.boundary.length,
      sprinkler_count: state.sprinklers.length,
      zone_count: state.zones.length,
      pipe_count: state.pipes.length,
      plot_width: state.plotWidth,
      plot_height: state.plotHeight,
    } : null

    const resp = await authenticatedFetch(api_v1_ai_chat_path(), {
      method: 'POST',
      body: {
        // The backend hands `messages` to the Messages API as-is, and that API
        // takes only user/assistant turns there and must open with a user one.
        // The trim summary sits at index 0 with role `system`, which it rejects
        // with a 400 — so every conversation broke at the eleventh question.
        // The summary is context about the conversation, so it goes as a user turn.
        messages: chatMessages.map((m) => ({
          role: m.role === 'system' ? 'user' : m.role,
          content: m.content,
        })),
        project_context: context,
        project_id: getProjectId(),
      },
    })

    checkRateLimitError(resp)
    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}))
      throw new Error(err.error || `HTTP ${resp.status}`)
    }

    const data = await resp.json()
    const reply = data.response || data.message || data.content || ''
    chatMessages.push({ role: 'assistant', content: reply })

    if (typingEl) typingEl.remove()
    appendChatMessage('assistant', reply)
    trackAiChat()
  } catch (e) {
    if (typingEl) typingEl.remove()
    if (!isRateLimited(e)) appendChatMessage('assistant', t('ai.chatError'))
  } finally {
    chatPending = false
    if (sendBtn) (sendBtn as HTMLButtonElement).disabled = false
    if (input) input.focus()
  }
}

function toggleChatPanel() {
  const panel = document.getElementById('aiChatPanel')
  if (!panel) return
  if (!panel.hidden) {
    closeChatPanel()
    return
  }
  panel.hidden = false
  const input = document.getElementById('aiChatInput')
  if (input) input.focus()
}

/** Hiding the panel while focus is inside it would drop focus onto <body>;
 *  it goes back to the button that opens the panel. */
function closeChatPanel() {
  const panel = document.getElementById('aiChatPanel')
  if (!panel || panel.hidden) return
  const hadFocus = panel.contains(document.activeElement)
  panel.hidden = true
  if (hadFocus) document.getElementById('aiChatBtn')?.focus()
}

// ── 4. BOM Optimization ─────────────────────────────────────────────

function createBomOptimizeButton() {
  const statsPanel = aiStatsAnchor()
  if (!statsPanel) return
  if (document.getElementById('btnAiOptimizeBom')) return

  const btn = document.createElement('button')
  btn.id = 'btnAiOptimizeBom'
  btn.className = 'ai-btn-secondary ai-bom-btn'
  btn.setAttribute('data-i18n', 'ai.optimizeBom')
  btn.textContent = tf('ai.optimizeBom', 'Optimize with AI')
  statsPanel.appendChild(btn)
}

function createBomOptimizeModal() {
  if (document.getElementById('aiBomOverlay')) return

  const overlay = document.createElement('div')
  overlay.id = 'aiBomOverlay'
  overlay.className = 'ai-overlay'
  overlay.hidden = true

  overlay.innerHTML = `
    <div class="ai-modal ai-bom-modal" role="dialog" aria-labelledby="aiBomTitle" aria-modal="true">
      <div class="ai-modal-header">
        <h3 id="aiBomTitle" data-i18n="ai.bomTitle">${t('ai.bomTitle')}</h3>
        <button class="ai-modal-close" id="aiBomClose" data-i18n="[aria-label]common.close" aria-label="Close">&times;</button>
      </div>
      <div class="ai-modal-body">
        <div class="ai-loading" id="aiBomLoading" hidden>
          <div class="ai-spinner"></div>
          <p data-i18n="ai.optimizing">${t('ai.optimizing')}</p>
        </div>
        <div id="aiBomResults" hidden></div>
        <div id="aiBomEmpty">
          <p data-i18n="ai.bomDesc">${t('ai.bomDesc')}</p>
          <button class="ai-btn-primary" id="aiBomStart" data-i18n="ai.startOptimization">${t('ai.startOptimization')}</button>
        </div>
      </div>
    </div>
  `
  document.body.appendChild(overlay)
}

function renderBomComparison(data: any) {
  const container = document.getElementById('aiBomResults')
  if (!container) return

  let html = ''

  // Cost summary header with current, optimized, and savings
  const savingsTotal = data.savings?.total ?? (data.current_cost && data.optimized_cost ? data.current_cost - data.optimized_cost : null)
  const savingsPct = data.savings_percent ?? data.savings?.percentage ?? 0

  if (data.current_cost !== null && data.current_cost !== undefined || data.optimized_cost !== null && data.optimized_cost !== undefined || savingsTotal !== null && savingsTotal !== undefined) {
    html += '<div class="ai-bom-cost-summary">'
    if (data.current_cost !== null && data.current_cost !== undefined) {
      html += `
        <div class="ai-bom-cost-item">
          <span class="ai-bom-cost-label">${t('ai.currentCost')}:</span>
          <span class="ai-bom-cost-value">$${Number(data.current_cost).toFixed(2)}</span>
        </div>`
    }
    if (data.optimized_cost !== null && data.optimized_cost !== undefined) {
      html += `
        <div class="ai-bom-cost-item">
          <span class="ai-bom-cost-label">${t('ai.optimizedCost')}:</span>
          <span class="ai-bom-cost-value ai-bom-cost-optimized">$${Number(data.optimized_cost).toFixed(2)}</span>
        </div>`
    }
    if (savingsTotal !== null && savingsTotal !== undefined) {
      html += `
        <div class="ai-bom-cost-item ai-bom-savings">
          <span class="ai-bom-savings-label">${t('ai.savings')}:</span>
          <span class="ai-bom-savings-value">$${Number(savingsTotal).toFixed(2)} (${savingsPct}%)</span>
        </div>`
    }
    html += '</div>'
  }

  // Recommendations table
  const recommendations = data.recommendations || data.alternatives || []
  if (recommendations.length > 0) {
    html += '<table class="ai-bom-table"><thead><tr>'
    html += `<th>${t('ai.bomItem')}</th>`
    html += `<th>${t('ai.bomAlternative')}</th>`
    html += `<th>${t('ai.bomReason')}</th>`
    html += `<th>${t('ai.savings')}</th>`
    html += '</tr></thead><tbody>'

    for (const rec of recommendations) {
      const item = rec.item || rec.original || ''
      const alt = rec.alternative || rec.suggested || ''
      const reason = rec.reason || ''
      const recSavings = rec.savings || rec.savings_per_unit || 0
      const impact = rec.coverage_impact || 'none'
      const impactClass = impact === 'none' ? '' : ' ai-bom-impact-warning'

      html += `<tr class="ai-bom-changed${impactClass}">`
      html += `<td>${escapeHtml(item)}</td>`
      html += `<td>${escapeHtml(alt)}</td>`
      html += `<td>${escapeHtml(reason)}</td>`
      html += `<td>$${Number(recSavings).toFixed(2)}</td>`
      html += '</tr>'
    }

    html += '</tbody></table>'
  }

  // Fallback: legacy "changes" format
  if (recommendations.length === 0 && data.changes && data.changes.length > 0) {
    html += '<table class="ai-bom-table"><thead><tr>'
    html += `<th>${t('ai.bomItem')}</th>`
    html += `<th>${t('ai.bomCurrent')}</th>`
    html += `<th>${t('ai.bomOptimized')}</th>`
    html += '</tr></thead><tbody>'

    for (const change of data.changes) {
      const highlight = change.changed ? ' class="ai-bom-changed"' : ''
      html += `<tr${highlight}>`
      html += `<td>${escapeHtml(change.name)}</td>`
      html += `<td>${escapeHtml(change.current)}</td>`
      html += `<td>${escapeHtml(change.optimized)}</td>`
      html += '</tr>'
    }

    html += '</tbody></table>'
  }

  if (data.explanation) {
    html += `<div class="ai-bom-explanation">${renderMarkdown(data.explanation)}</div>`
  }

  // Advice only. An "Apply Optimization" button sat here and fired an
  // `ai:applyBom` event that nothing listened for, then toasted success.
  // There is nothing to apply it to: the planner keeps no bill of materials
  // (the PDF export builds one from the design), and the suggested items are
  // free-text names that cannot be mapped back onto heads, valves or pipes.
  html += `
    <div class="ai-placement-actions">
      <button class="ai-btn-secondary" id="aiBomDismiss" data-i18n="ai.dismiss">${t('ai.dismiss')}</button>
    </div>
  `

  container.innerHTML = html

  const dismissBtn = document.getElementById('aiBomDismiss')
  if (dismissBtn) {
    dismissBtn.addEventListener('click', () => {
      hideModal('aiBomOverlay')
    })
  }
}

async function runBomOptimization() {
  const loading = document.getElementById('aiBomLoading')
  const results = document.getElementById('aiBomResults')
  const empty = document.getElementById('aiBomEmpty')

  const state = getProjectState()
  if (!state || state.sprinklers.length === 0) {
    showToast(t('ai.noSprinklers'), 'warning')
    return
  }

  if (loading) loading.hidden = false
  if (results) results.hidden = true
  if (empty) empty.hidden = true

  try {
    const resp = await authenticatedFetch(api_v1_ai_optimize_bom_path(), {
      method: 'POST',
      body: {
        project_id: getProjectId(),
        sprinklers: state.sprinklers,
        zones: state.zones,
        pipes: state.pipes,
        valve_objects: state.valveObjects,
        trees: state.trees,
        plot_width: state.plotWidth,
        plot_height: state.plotHeight,
      },
    })

    checkRateLimitError(resp)
    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}))
      throw new Error(err.error || `HTTP ${resp.status}`)
    }

    const raw = await resp.json()
    const data = raw.optimization || raw
    renderBomComparison(data)
    if (results) results.hidden = false
  } catch (e) {
    if (!isRateLimited(e)) showToast(t('ai.bomError'), 'error')
    if (empty) empty.hidden = false
  } finally {
    if (loading) loading.hidden = true
  }
}

// ── 5. NLP Command Input ─────────────────────────────────────────────

function createNlpCommandButton() {
  const actionsGroup = aiToolbarAnchor()
  if (!actionsGroup) return
  if (document.getElementById('btnAiNlpCommand')) return

  const btn = document.createElement('button')
  btn.id = 'btnAiNlpCommand'
  btn.className = 'action-btn ai-action-btn'
  btn.setAttribute('title', t('ai.nlpCommandTitle'))
  btn.setAttribute('aria-label', t('ai.nlpCommand'))
  btn.setAttribute('data-i18n', '[aria-label]ai.nlpCommand;[title]ai.nlpCommandTitle')
  btn.innerHTML = `
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <path d="M12 20h9"/>
      <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>
    </svg>
    <span data-i18n="ai.nlpCommand">NLP</span>
  `

  actionsGroup.appendChild(btn)
}

function createNlpCommandModal() {
  if (document.getElementById('aiNlpOverlay')) return

  const overlay = document.createElement('div')
  overlay.id = 'aiNlpOverlay'
  overlay.className = 'ai-overlay'
  overlay.hidden = true

  const examplesHtml = [
    { key: 'ai.nlpExample1', fallback: 'Create a 20 by 15 m rectangular lawn' },
    { key: 'ai.nlpExample2', fallback: 'Add MP2000 sprinklers along the north edge' },
    { key: 'ai.nlpExample3', fallback: 'Change all nozzles to MP3000' },
    { key: 'ai.nlpExample4', fallback: 'Split the sprinklers into two zones' },
  ].map(({ key, fallback }) => {
    const label = tf(key, fallback)
    return `<button class="ai-chip ai-nlp-example" data-command="${escapeHtml(label)}" data-i18n="[data-command]${key};${key}">${escapeHtml(label)}</button>`
  }).join('')

  overlay.innerHTML = `
    <div class="ai-modal ai-nlp-modal" role="dialog" aria-labelledby="aiNlpTitle" aria-modal="true">
      <div class="ai-modal-header">
        <h3 id="aiNlpTitle" data-i18n="ai.nlpTitle">${t('ai.nlpTitle')}</h3>
        <button class="ai-modal-close" id="aiNlpClose" data-i18n="[aria-label]common.close" aria-label="Close">&times;</button>
      </div>
      <div class="ai-modal-body">
        <div class="ai-loading" id="aiNlpLoading" hidden>
          <div class="ai-spinner"></div>
          <p data-i18n="ai.nlpProcessing">${t('ai.nlpProcessing')}</p>
        </div>
        <div id="aiNlpForm">
          <p class="ai-nlp-description" data-i18n="ai.nlpDescription">${t('ai.nlpDescription')}</p>
          <div class="ai-nlp-examples">${examplesHtml}</div>
          <div class="ai-form-field">
            <label for="aiNlpInput" data-i18n="ai.nlpInputLabel">${t('ai.nlpInputLabel')}</label>
            <div class="ai-input-group">
              <input type="text" id="aiNlpInput" class="ai-input ai-nlp-input"
                placeholder="${t('ai.nlpPlaceholder')}"
                data-i18n="[placeholder]ai.nlpPlaceholder"
                autocomplete="off">
              <button class="ai-btn-primary ai-nlp-send" id="aiNlpSend" data-i18n="ai.nlpExecute">${t('ai.nlpExecute')}</button>
            </div>
          </div>
        </div>
        <div id="aiNlpResults" hidden>
          <div id="aiNlpMessage" class="ai-nlp-message"></div>
          <div id="aiNlpActions" class="ai-nlp-actions-list"></div>
          <div class="ai-placement-actions">
            <button class="ai-btn-secondary" id="aiNlpReject" data-i18n="ai.reject">${t('ai.reject')}</button>
            <button class="ai-btn-primary" id="aiNlpAccept" data-i18n="ai.nlpApply">${t('ai.nlpApply')}</button>
          </div>
        </div>
      </div>
    </div>
  `
  document.body.appendChild(overlay)
}

interface NlpAction {
  type: string;
  params?: Record<string, unknown>;
}

type PlanPoint = { x: number; y: number }
type PlanSprinkler = PlanPoint & { model?: string; arc?: number; radius?: number; zoneId?: number }
interface PlannerEntryPoints {
  applyBoundary?: (points: PlanPoint[]) => void
  addSprinklers?: (sprinklers: PlanSprinkler[]) => void
}

let pendingNlpActions: NlpAction[] | null = null

/** A number the model sent as a number or a numeric string, else null. */
function planNumber(value: unknown): number | null {
  if (typeof value !== 'number' && (typeof value !== 'string' || value.trim() === '')) return null
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

function planPoints(raw: unknown): PlanPoint[] | null {
  if (!Array.isArray(raw) || raw.length < 3) return null
  const points = raw.map((p) => ({ x: planNumber(p?.x), y: planNumber(p?.y) }))
  return points.every((p) => p.x !== null && p.y !== null) ? (points as PlanPoint[]) : null
}

function planSprinklers(raw: unknown): PlanSprinkler[] {
  if (!Array.isArray(raw)) return []
  return raw.flatMap((s) => {
    const x = planNumber(s?.x)
    const y = planNumber(s?.y)
    if (x === null || y === null) return []
    const sprinkler: PlanSprinkler = { x, y }
    if (typeof s.model === 'string' && s.model) sprinkler.model = s.model
    const arc = planNumber(s.arc)
    if (arc !== null) sprinkler.arc = arc
    const radius = planNumber(s.radius)
    if (radius !== null && radius > 0) sprinkler.radius = radius
    const zoneId = planNumber(s.zone_id)
    if (zoneId !== null) sprinkler.zoneId = zoneId
    return [sprinkler]
  })
}

/** create_project names one of the planner's plot templates (with `_` for
 *  `-`) and a size; the template's outline is stretched to that size. */
function planOutline(shape: unknown, width: unknown, height: unknown): PlanPoint[] | null {
  const w = planNumber(width)
  const h = planNumber(height)
  const template = PLOT_TEMPLATES.find((tpl) => tpl.key === String(shape).replace(/_/g, '-'))
  if (!template || w === null || h === null || w <= 0 || h <= 0) return null
  const xs = template.points.map((p) => p.x)
  const ys = template.points.map((p) => p.y)
  const minX = Math.min(...xs)
  const minY = Math.min(...ys)
  const scaleX = w / (Math.max(...xs) - minX)
  const scaleY = h / (Math.max(...ys) - minY)
  const round = (v: number) => Math.round(v * 100) / 100
  return template.points.map((p) => ({ x: round((p.x - minX) * scaleX), y: round((p.y - minY) * scaleY) }))
}

/** How one proposed action lands on the plan, or null when it cannot. The
 *  planner takes a boundary and new heads from outside — templates and smart
 *  placement use the same two entry points — but has no way in for removing
 *  heads, swapping nozzles or editing zones: zones are drawn outlines, which a
 *  list of sprinkler ids cannot become. Those stay listed for the user. */
function nlpApplier(action: NlpAction): (() => void) | null {
  const planner = ((window as any).__smartpluvia ?? {}) as PlannerEntryPoints
  const params = action.params ?? {}
  switch (action.type) {
    case 'add_boundary': {
      const points = planPoints(params.points)
      return points && planner.applyBoundary ? () => planner.applyBoundary!(points) : null
    }
    case 'add_sprinklers': {
      const sprinklers = planSprinklers(params.sprinklers)
      return sprinklers.length && planner.addSprinklers ? () => planner.addSprinklers!(sprinklers) : null
    }
    case 'create_project': {
      const outline = planOutline(params.shape, params.width, params.height)
      if (!outline || !planner.applyBoundary) return null
      const sprinklers = planSprinklers(params.sprinklers)
      return () => {
        planner.applyBoundary!(outline)
        if (sprinklers.length) planner.addSprinklers?.(sprinklers)
      }
    }
    default:
      return null
  }
}

function renderNlpResults(data: any) {
  const messageEl = document.getElementById('aiNlpMessage')
  const actionsEl = document.getElementById('aiNlpActions')
  if (!messageEl || !actionsEl) return

  // Both are rewritten every time: a reply without actions used to leave the
  // previous command's list on screen.
  messageEl.innerHTML = data.message ? renderMarkdown(data.message) : ''

  const actions: NlpAction[] = Array.isArray(data.actions) ? data.actions : []
  let html = ''
  if (actions.length > 0) {
    html = `<p class="ai-nlp-actions-title">${t('ai.nlpActionsTitle')}:</p><ul>`
    for (const action of actions) {
      const actionLabel = String(action.type ?? '').replace(/_/g, ' ')
      html += `<li><strong>${escapeHtml(actionLabel)}</strong>`
      if (action.params) {
        const summary = summarizeActionParams(action)
        if (summary) html += ` — ${escapeHtml(summary)}`
      }
      if (!nlpApplier(action)) html += ` <em class="ai-nlp-manual">${escapeHtml(t('ai.nlpManual'))}</em>`
      html += '</li>'
    }
    html += '</ul>'
  }
  actionsEl.innerHTML = html

  // Apply is offered only when it will change something.
  const acceptBtn = document.getElementById('aiNlpAccept')
  if (acceptBtn) acceptBtn.hidden = !actions.some((action) => nlpApplier(action))
}

function summarizeActionParams(action: any) {
  const p = action.params
  switch (action.type) {
    case 'create_project':
      return `${p.shape || 'rectangle'} ${formatLength(Number(p.width) || 0)} × ${formatLength(Number(p.height) || 0)}`
    case 'add_sprinklers':
      return `${(p.sprinklers || []).length} sprinkler(s)`
    case 'remove_sprinklers':
      if (p.ids) return `${p.ids.length} by ID`
      if (p.filter?.model) return `model: ${p.filter.model}`
      return ''
    case 'modify_zone':
      return p.name || ''
    case 'change_nozzle_type':
      return `to ${p.new_model}`
    case 'add_boundary':
      return `${(p.points || []).length} points`
    default:
      return ''
  }
}

async function runNlpCommand(text: string) {
  if (!text || !text.trim()) return

  const loading = document.getElementById('aiNlpLoading')
  const form = document.getElementById('aiNlpForm')
  const results = document.getElementById('aiNlpResults')

  if (form) form.hidden = true
  if (loading) loading.hidden = false
  if (results) results.hidden = true

  try {
    const state = getProjectState()
    const projectData = state ? {
      boundary: state.boundary,
      sprinklers: state.sprinklers,
      zones: state.zones,
      pipes: state.pipes,
      plot_width: state.plotWidth,
      plot_height: state.plotHeight,
    } : null

    const currentLang = getDocumentLanguage().substring(0, 2)

    const resp = await authenticatedFetch(api_v1_ai_nlp_command_path(), {
      method: 'POST',
      body: {
        command: text.trim(),
        project_data: projectData,
        lang: currentLang,
      },
    })

    checkRateLimitError(resp)
    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}))
      throw new Error(err.error || `HTTP ${resp.status}`)
    }

    const data = await resp.json()
    pendingNlpActions = data.actions || []

    renderNlpResults(data)
    if (results) results.hidden = false
  } catch (e) {
    if (!isRateLimited(e)) showToast(t('ai.nlpError'), 'error')
    if (form) form.hidden = false
  } finally {
    if (loading) loading.hidden = true
  }
}

// Apply used to dispatch an `ai:nlpAction` event per action that nothing in
// the app listened for, then report success: the plan never changed.
function acceptNlpActions() {
  if (!pendingNlpActions || pendingNlpActions.length === 0) return

  const total = pendingNlpActions.length
  const appliers = pendingNlpActions.map(nlpApplier).filter((apply): apply is () => void => apply !== null)
  if (appliers.length === 0) return
  appliers.forEach((apply) => apply())
  pendingNlpActions = null
  showToast(
    appliers.length === total
      ? t('ai.nlpApplied')
      : t('ai.nlpAppliedPartial', { applied: appliers.length, total }),
    'success',
  )

  hideModal('aiNlpOverlay')
}

function rejectNlpActions() {
  pendingNlpActions = null

  const form = document.getElementById('aiNlpForm')
  const results = document.getElementById('aiNlpResults')
  if (form) form.hidden = false
  if (results) results.hidden = true
}

// ── Event Listeners ─────────────────────────────────────────────────

function attachEventListeners() {
  // --- Analysis ---
  const analysisBtn = document.getElementById('btnAiAnalysis')
  const analysisOverlay = document.getElementById('aiAnalysisOverlay')
  const analysisClose = document.getElementById('aiAnalysisClose')
  const analysisStart = document.getElementById('aiAnalysisStart')

  if (analysisBtn) {
    analysisBtn.addEventListener('click', () => showModal('aiAnalysisOverlay'))
  }
  if (analysisClose) {
    analysisClose.addEventListener('click', () => hideModal('aiAnalysisOverlay'))
  }
  if (analysisOverlay) {
    analysisOverlay.addEventListener('click', (e) => {
      if (e.target === analysisOverlay) hideModal('aiAnalysisOverlay')
    })
  }
  if (analysisStart) {
    analysisStart.addEventListener('click', () => runAnalysis())
  }

  // --- Smart Place ---
  const smartPlaceBtn = document.getElementById('btnAiSmartPlace')
  const smartPlaceOverlay = document.getElementById('aiSmartPlaceOverlay')
  const smartPlaceClose = document.getElementById('aiSmartPlaceClose')
  const smartPlaceStart = document.getElementById('aiSmartPlaceStart')
  const placementAccept = document.getElementById('aiPlacementAccept')
  const placementReject = document.getElementById('aiPlacementReject')

  if (smartPlaceBtn) {
    // The proposed heads are drawn onto the planner canvas, so every way
    // out of the modal has to take them off again.
    smartPlaceBtn.addEventListener('click', () => showModal('aiSmartPlaceOverlay', { onHide: clearPlacementPreview }))
  }
  if (smartPlaceClose) {
    smartPlaceClose.addEventListener('click', () => hideModal('aiSmartPlaceOverlay'))
  }
  if (smartPlaceOverlay) {
    smartPlaceOverlay.addEventListener('click', (e) => {
      if (e.target === smartPlaceOverlay) hideModal('aiSmartPlaceOverlay')
    })
  }
  if (smartPlaceStart) {
    smartPlaceStart.addEventListener('click', () => runSmartPlace())
  }
  if (placementAccept) {
    placementAccept.addEventListener('click', () => acceptPlacement())
  }
  if (placementReject) {
    placementReject.addEventListener('click', () => rejectPlacement())
  }

  // --- Chat ---
  const chatBtn = document.getElementById('aiChatBtn')
  const chatClose = document.getElementById('aiChatClose')
  const chatSend = document.getElementById('aiChatSend')
  const chatInput = document.getElementById('aiChatInput')

  if (chatBtn) {
    chatBtn.addEventListener('click', () => toggleChatPanel())
  }
  if (chatClose) {
    chatClose.addEventListener('click', () => closeChatPanel())
  }
  if (chatSend) {
    chatSend.addEventListener('click', () => {
      const input = document.getElementById('aiChatInput')
      if (input) sendChatMessage((input as HTMLInputElement).value)
    })
  }
  if (chatInput) {
    chatInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        sendChatMessage((chatInput as HTMLInputElement).value)
      }
    })
  }

  // Suggestion chips (chat only, not NLP examples)
  document.addEventListener('click', (e) => {
    if ((e.target! as HTMLElement).classList.contains('ai-chip') && !(e.target! as HTMLElement).classList.contains('ai-nlp-example')) {
      const message = (e.target! as HTMLElement).dataset.message || (e.target! as HTMLElement).textContent
      sendChatMessage(message)
    }
  })

  // --- BOM Optimization ---
  const bomBtn = document.getElementById('btnAiOptimizeBom')
  const bomOverlay = document.getElementById('aiBomOverlay')
  const bomClose = document.getElementById('aiBomClose')
  const bomStart = document.getElementById('aiBomStart')

  if (bomBtn) {
    bomBtn.addEventListener('click', () => showModal('aiBomOverlay'))
  }
  if (bomClose) {
    bomClose.addEventListener('click', () => hideModal('aiBomOverlay'))
  }
  if (bomOverlay) {
    bomOverlay.addEventListener('click', (e) => {
      if (e.target === bomOverlay) hideModal('aiBomOverlay')
    })
  }
  if (bomStart) {
    bomStart.addEventListener('click', () => runBomOptimization())
  }

  // --- NLP Command ---
  const nlpBtn = document.getElementById('btnAiNlpCommand')
  const nlpOverlay = document.getElementById('aiNlpOverlay')
  const nlpClose = document.getElementById('aiNlpClose')
  const nlpSend = document.getElementById('aiNlpSend')
  const nlpInput = document.getElementById('aiNlpInput')
  const nlpAccept = document.getElementById('aiNlpAccept')
  const nlpReject = document.getElementById('aiNlpReject')

  if (nlpBtn) {
    nlpBtn.addEventListener('click', () => {
      showModal('aiNlpOverlay')
      if (nlpInput) nlpInput.focus()
    })
  }
  if (nlpClose) {
    nlpClose.addEventListener('click', () => hideModal('aiNlpOverlay'))
  }
  if (nlpOverlay) {
    nlpOverlay.addEventListener('click', (e) => {
      if (e.target === nlpOverlay) hideModal('aiNlpOverlay')
    })
  }
  if (nlpSend) {
    nlpSend.addEventListener('click', () => {
      if (nlpInput) runNlpCommand((nlpInput as HTMLInputElement).value)
    })
  }
  if (nlpInput) {
    nlpInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        runNlpCommand((nlpInput as HTMLInputElement).value)
      }
    })
  }
  if (nlpAccept) {
    nlpAccept.addEventListener('click', () => acceptNlpActions())
  }
  if (nlpReject) {
    nlpReject.addEventListener('click', () => rejectNlpActions())
  }

  // NLP example chips
  document.addEventListener('click', (e) => {
    if ((e.target! as HTMLElement).classList.contains('ai-nlp-example')) {
      const command = (e.target! as HTMLElement).dataset.command || (e.target! as HTMLElement).textContent
      if (nlpInput) (nlpInput as HTMLInputElement).value = command || ''
      runNlpCommand(command || '')
    }
  })

  // --- Escape from outside a panel ---
  // An Escape pressed inside a modal is handled — and stopped — by
  // showModal(); this covers the chat panel and any key that arrives while
  // focus sits elsewhere on the page.
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return
    const openOverlay = AI_OVERLAY_IDS.find((id) => isOpenModal(id))
    if (openOverlay) {
      hideModal(openOverlay)
    } else {
      closeChatPanel()
    }
  })
}

// ── Public Init ─────────────────────────────────────────────────────

export function initAiFeatures() {
  // Toolbar buttons
  createAnalysisButton()
  createSmartPlaceButton()
  createNlpCommandButton()

  // Modals
  createAnalysisModal()
  createSmartPlaceModal()
  createBomOptimizeModal()
  createNlpCommandModal()

  // Floating chat
  createChatButton()
  createChatPanel()

  // BOM sidebar button
  createBomOptimizeButton()

  // Wire up events
  attachEventListeners()

  // Apply translations to newly created elements
  applyTranslations()
}
